import { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useImpersonateUserMutation } from "../impersonation/impersonationApiSlice";
import { useImpersonationHandoff } from "../impersonation/useImpersonationHandoff";
import { DemoUserViewModel } from "./demoApiSlice";

interface DemoUsersTableProps {
	users: DemoUserViewModel[];
}

// Password is hidden by default and revealed on demand. Each cell owns its
// reveal state so rows toggle independently. Hashed accounts have no plaintext
// password to show, so we render a dash with no toggle.
function PasswordCell({ password }: { password: string }) {
	const [revealed, setRevealed] = useState(false);

	if (!password) {
		return <span className="text-gray-400">—</span>;
	}

	return (
		<div className="flex items-center gap-2">
			<span>{revealed ? password : "••••••••"}</span>
			<button
				type="button"
				className="cursor-pointer text-gray-400 hover:text-gray-600"
				aria-label={revealed ? "Hide password" : "Show password"}
				onClick={() => setRevealed((prev) => !prev)}>
				{revealed ? (
					<EyeIcon className="size-5" aria-hidden="true" />
				) : (
					<EyeSlashIcon className="size-5" aria-hidden="true" />
				)}
			</button>
		</div>
	);
}

export default function DemoUsersTable(props: DemoUsersTableProps) {
	const [signingInUserId, setSigningInUserId] = useState<number | null>(null);

	const [impersonateUser] = useImpersonateUserMutation();
	const { handleImpersonationResult, showImpersonationError } = useImpersonationHandoff();

	const impersonateErrorMessage = "There was an error signing in as this user.";

	const handleImpersonate = (userId: number) => {
		setSigningInUserId(userId);
		impersonateUser(userId)
			.unwrap()
			.then((result) => {
				// On success the app navigates away; otherwise re-enable the button.
				if (!handleImpersonationResult(result, impersonateErrorMessage)) {
					setSigningInUserId(null);
				}
			})
			.catch(() => {
				showImpersonationError(undefined, impersonateErrorMessage);
				setSigningInUserId(null);
			});
	};

	const passwordBodyTemplate = (row: DemoUserViewModel) => {
		return <PasswordCell password={row.password} />;
	};

	const actionBodyTemplate = (row: DemoUserViewModel) => {
		return (
			<button
				id={`impersonate-${row.id}`}
				className="btn btn-action !px-2"
				disabled={signingInUserId === row.id}
				onClick={() => handleImpersonate(row.id)}>
				{signingInUserId === row.id ? "Signing In…" : "Sign In"}
			</button>
		);
	};

	return (
		<DataTable
			value={props.users}
			dataKey="id"
			stripedRows
			emptyMessage="No demo users found"
			className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
			style={{ paddingTop: "16px" }}>
			<Column header="Role" field="role" headerClassName="!px-2" className="!px-2"></Column>
			<Column
				header="Username"
				field="username"
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				header="Password"
				body={passwordBodyTemplate}
				headerClassName="!px-2"
				className="!px-2"></Column>
			<Column
				body={actionBodyTemplate}
				headerClassName="!px-2 w-24"
				className="!px-2"></Column>
		</DataTable>
	);
}
